import { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'

interface TeamResponse {
  id: string
  name: string
}

export function TeamSetupPage() {
  const [teamName, setTeamName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { refreshProfile } = useAuth()

  const emailConfirmed = useMemo(() => {
    return searchParams.get('confirmed') === 'true'
  }, [searchParams])

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const supabase = createClient()

    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      setError('Please sign in to create a team')
      setLoading(false)
      return
    }

    // Create team - use rpc or raw query to avoid type issues
    const { data: team, error: teamError } = await (supabase
      .from('teams') as ReturnType<typeof supabase.from>)
      .insert({ name: teamName })
      .select()
      .single()

    if (teamError || !team) {
      setError(teamError?.message || 'Failed to create team')
      setLoading(false)
      return
    }

    const teamData = team as TeamResponse

    // Create membership
    const { error: membershipError } = await (supabase
      .from('memberships') as ReturnType<typeof supabase.from>)
      .insert({
        user_id: user.id,
        team_id: teamData.id,
        role: 'owner',
      })

    if (membershipError) {
      setError(membershipError.message)
      setLoading(false)
      return
    }

    // Refresh profile to get the new membership
    await refreshProfile()
    // Navigate to calendar setup as next onboarding step
    navigate('/calendar-setup')
  }

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-8 shadow-sm">
      <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
        Set up your team
      </h1>
      <p className="text-zinc-500 mb-6">Create a team to start managing your podcast guests</p>

      <form onSubmit={handleCreateTeam} className="space-y-4">
        {emailConfirmed && (
          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm">
            Your email has been confirmed. Now let&apos;s set up your team!
          </div>
        )}
        {error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="teamName"
            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
          >
            Team name
          </label>
          <input
            id="teamName"
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="My Podcast Team"
            required
          />
          <p className="mt-1 text-xs text-zinc-500">
            This is your podcast production team or organization name
          </p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2 px-4 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating team...' : 'Create team'}
        </button>
      </form>
    </div>
  )
}
